import type { WorklistIssue } from "../../studio/cellState"

/**
 * The "Weiter" button's successor pick (TUR-1449 PR 3): given where the
 * reviewer stands, the next open point to jump to — statement (confirm /
 * scope / fill fields, deduped to one stop per card), the framework's
 * reference-list conflict, or, with nothing left in the worklist, the
 * approve button.
 *
 * DELIBERATELY NOT `countOpenPoints`' list: Weiter only targets points the
 * reviewer can act on in the mask. The unmatched-supplier pseudo-item (its
 * popover row is an assign control, not a jump) and open Rückfragen (waiting
 * on the supplier, not on the reviewer) are skipped — the counter, the
 * banner and the approve button's own refusal reason keep naming them, and
 * the approve terminal (kind "approve") routes the reviewer straight to the
 * control that does.
 *
 * Order is FRAMEWORK-MAJOR from the active Verordnung, wrapping — not the
 * worklist's own global step-major sort: Weiter's promise is "finish where
 * you stand, then move on", not "revisit every framework once per step".
 * Within a framework the wider unit goes first (the edition conflict
 * reframes the statements under it — same rationale as `deriveWorklist`'s
 * `unitRank`), then statements in DISPLAY order (`cardOrderOf` — the same
 * evidence-page order the cards themselves render in, TUR-1211 W2-3), several
 * steps on one card deduped into a single stop. A statement id `cardOrderOf`
 * doesn't know sorts after every listed one, in its own groupIndex order —
 * `cardOrderOf`'s default (every existing caller) returns no ids at all, so
 * every statement takes that fallback and the order is unchanged from before
 * this id-order concept existed: plain groupIndex.
 *
 * Self-as-successor is legal: if the sole open point is the card the
 * reviewer is on, it comes back — the caller re-focuses it visibly instead
 * of doing nothing (the never-stuck rule applied to a button press).
 */
export type OpenPointTarget =
  | { kind: "framework"; framework: string; unitId: string }
  | {
      kind: "statement"
      framework: string
      statementId: string
      /** W5-4: the blocking step a direct caller (the approve gate, the
       *  open-points popover) names, so the landed card also opens that
       *  step's own dialog (`UnitCard`'s own `focusStep`). `nextOpenPoint`
       *  itself never sets this: Weiter's own promise is "finish where you
       *  stand" (this function's own module doc), unchanged by W5-4. */
      step?: 1 | 2 | 3
    }
  | { kind: "approve" }

/**
 * One framework's worklist issues in the order the reviewer SEES them: the
 * framework unit first (the edition conflict reframes the statements under
 * it), then statements in DISPLAY order (`order`: the card list's own ids,
 * evidence-page sorted since TUR-1211 W2-3a), ids the list doesn't carry
 * after every listed one in their own groupIndex order; several steps on one
 * card then sort by step ascending (W5-4), so the FIRST of a card's own
 * issues is always its most urgent one, whatever order they arrived in. The
 * ONE comparator behind Weiter (`nextOpenPoint`) and the open-points map
 * (`OpenPointsPopover`),
 * so the map can never list "Aussage 3, 1, 2" while Weiter walks 1, 2, 3
 * (review, wave 2).
 */
export function issuesInDisplayOrder(
  issues: readonly WorklistIssue[],
  order: readonly string[],
): WorklistIssue[] {
  const rankOf = new Map(order.map((id, i) => [id, i] as const))
  const rank = (i: WorklistIssue) => rankOf.get(i.statementId ?? i.unitId)
  return [...issues].sort((a, b) => {
    if (a.unit !== b.unit) return a.unit === "framework" ? -1 : 1
    const rankA = rank(a)
    const rankB = rank(b)
    if (rankA !== undefined && rankB !== undefined)
      return rankA - rankB || a.step - b.step
    if (rankA !== undefined) return -1
    if (rankB !== undefined) return 1
    return (a.groupIndex ?? -1) - (b.groupIndex ?? -1) || a.step - b.step
  })
}

/**
 * A framework's open worklist issues, grouped by statement id: the ONE
 * grouping both the collapsed unit card's open-points chip (`UnitCard`'s own
 * `openIssues` prop, via `ReviewMaskPane`) and the open-points popover read,
 * so the two can never disagree about which card owns which issues or which
 * one of them is "first" (W5-4). Each card's own issue list, and the map's
 * own key order, follow `issuesInDisplayOrder`, so within one card the
 * FIRST entry is the most urgent (lowest-step) open issue, matching
 * `deriveWorklist`'s step-major sort (stable, so two issues of the same card
 * keep their relative step order through the display re-sort). Framework-
 * unit issues (the reference-list conflict, no `statementId`) are skipped:
 * there is no card to group them under.
 *
 * Deliberately NOT the same count as `openCounts` in
 * `reviewmask/substanceTable.ts`: that one is the substance table's
 * display badge over condition fields, this one drives navigation and the
 * approve gate from `flowState`. They answer different questions and will
 * disagree, so do not merge them.
 */
export function openIssuesByCard(
  issues: readonly WorklistIssue[],
  order: readonly string[] = [],
): Map<string, WorklistIssue[]> {
  const statementIssues = issues.filter((i) => i.unit === "statement")
  const out = new Map<string, WorklistIssue[]>()
  for (const issue of issuesInDisplayOrder(statementIssues, order)) {
    const id = issue.statementId ?? issue.unitId
    const list = out.get(id)
    if (list) list.push(issue)
    else out.set(id, [issue])
  }
  return out
}

export function nextOpenPoint(input: {
  worklist: readonly WorklistIssue[]
  frameworks: readonly string[]
  activeFramework: string | null
  focusedStatementId: string | null
  /** Statement ids in DISPLAY order for a framework — the same order the
   *  card list itself renders (`state.statementCardsByFramework`, sorted by
   *  evidencePage since TUR-1211 W2-3a). An id this list doesn't carry sorts
   *  after every id it does, in that id's own groupIndex order. Optional
   *  with a default that returns no ids for every framework, so every
   *  existing caller/test keeps today's plain-groupIndex order unchanged. */
  cardOrderOf?: (framework: string) => readonly string[]
}): OpenPointTarget {
  const {
    worklist,
    frameworks,
    activeFramework,
    focusedStatementId,
    cardOrderOf = () => [],
  } = input

  const activeIdx = activeFramework ? frameworks.indexOf(activeFramework) : -1
  const rotated =
    activeIdx > 0
      ? [...frameworks.slice(activeIdx), ...frameworks.slice(0, activeIdx)]
      : frameworks

  const targets: Exclude<OpenPointTarget, { kind: "approve" }>[] = []
  for (const fw of rotated) {
    const issues = worklist.filter((i) => i.framework === fw)
    const fwUnit = issues.find((i) => i.unit === "framework")
    if (fwUnit) {
      // Carries the issue's own unitId — the URL's &stmt= addresses the UNIT
      // (see WorklistIssue's doc), so landing on this target writes that id
      // and the NEXT press can find its position and move past it. Matching
      // only statement targets here made a framework target an absorbing
      // state: stmt was nulled, currentIdx stayed -1, and every further
      // press returned targets[0] — this same target, forever.
      targets.push({ kind: "framework", framework: fw, unitId: fwUnit.unitId })
    }
    const seen = new Set<string>()
    const statements = issuesInDisplayOrder(issues, cardOrderOf(fw)).filter(
      (i) => i.unit === "statement",
    )
    for (const issue of statements) {
      // The card id the URL's &stmt= and the unit anchors address — same
      // resolution `advanceToNextOpenStatement` uses.
      const id = issue.statementId ?? issue.unitId
      if (seen.has(id)) continue
      seen.add(id)
      targets.push({ kind: "statement", framework: fw, statementId: id })
    }
  }

  if (targets.length === 0) return { kind: "approve" }

  // With no focused unit the reviewer stands BEFORE the rotation's first
  // target (currentIdx -1), so the first press lands on it — including the
  // active framework's own edition conflict — instead of skipping past it.
  // `focusedStatementId` is really the URL's &stmt= UNIT id: a statement
  // row id, or a framework unit id after a framework-target jump.
  const currentIdx = focusedStatementId
    ? targets.findIndex((t) =>
        t.kind === "statement"
          ? t.framework === activeFramework &&
            t.statementId === focusedStatementId
          : t.unitId === focusedStatementId,
      )
    : -1
  return targets[(currentIdx + 1) % targets.length]
}
